import React, { FC, useEffect, useCallback, useMemo, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";

import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import GroupIcon from "@mui/icons-material/Group";
import ListIcon from "@mui/icons-material/List";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EventIcon from "@mui/icons-material/Event";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Fade from "@mui/material/Fade";
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../store";
import {
  getTournoi,
  fetchTournoi,
  fetchTournoiByKey,
  getRegistrationPhase,
} from "../../features/tournoi/tournoiSlice";
import {
  setAdmin,
  admin,
  clearSession,
} from "../../features/session/sessionSlice";
import {
  Chip,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, logout } from "../../services/firebaseAuth";
import BarChartIcon from "@mui/icons-material/BarChart";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { usePageTitle } from "../../hooks/usePageTitle";
import PageBreadcrumbs from "../Base/PageBreadcrumbs";
import GlobalSnackbar from "../Base/GlobalSnackbar";
import { useColorMode } from "../../contexts/ColorModeContext";

interface LayoutProps {}

const drawerWidth = 240;
type NavItem = {
  label: string;
  path: string;
  hideOnMobile?: boolean;
  hideWhenRegistrationClosed?: boolean;
  /** Masqué tant que les inscriptions joueurs ne sont pas closes (phase ≠ closedForPlayers). */
  requiresClosedForPlayersPhase?: boolean;
  /** Réservé aux visiteurs connectés (ex. liste des inscrits). */
  requiresSignedIn?: boolean;
};

const navItems: NavItem[] = [
  { label: "Inscription", path: "/register", hideWhenRegistrationClosed: true },
  { label: "Inscrits", path: "/inscrits", requiresSignedIn: true },
  { label: "Tableaux", path: "/boards" },
  { label: "Résultats", path: "/resultats" },
  { label: "Règlement", path: "/rules" },
  {
    label: "Où jouez-vous ?",
    path: "/live-find",
    requiresClosedForPlayersPhase: true,
  },
  { label: "Nous contacter", path: "/contactus" },
];

type AdminNavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
};

const adminNavGroups: { groupLabel: string; items: AdminNavItem[] }[] = [
  {
    groupLabel: "Inscriptions",
    items: [
      { label: "Liste des inscrits", path: "/admin/registrations", icon: <GroupIcon /> },
      { label: "Liste des inscriptions", path: "/admin/registration-list", icon: <ListIcon /> },
      { label: "Inscrits par tableaux", path: "/admin/board-registrations", icon: <AssignmentIcon /> },
      {
        label: "Croisements 2 tableaux",
        path: "/admin/board-overlaps",
        icon: <CompareArrowsIcon />,
      },
      { label: "Listes d'attente", path: "/admin/waitlist", icon: <HourglassTopIcon /> },
    ],
  },
  {
    groupLabel: "Tournoi & tableaux",
    items: [
      { label: "Gérer le tournoi", path: "/admin/tournament-managment", icon: <EventIcon /> },
      {
        label: "Remboursements Stripe",
        path: "/admin/tournament-managment#remboursements",
        icon: <CurrencyExchangeIcon />,
      },
      {
        label: "Définition des tableaux",
        path: "/admin/boards-definitions",
        icon: <EditNoteIcon />,
      },
      {
        label: "Capacité et ouverture",
        path: "/admin/boards-managment",
        icon: <ToggleOnIcon />,
      },
    ],
  },
  {
    groupLabel: "Matchs & pointage",
    items: [
      { label: "Gérer les matchs", path: "/admin/matchs", icon: <SportsTennisIcon /> },
      { label: "Pointage", path: "/admin/checkin", icon: <HowToRegIcon /> },
      {
        label: "Occupation des tables",
        path: "/admin/tables-occupancy",
        icon: <ViewTimelineIcon />,
      },
    ],
  },
  {
    groupLabel: "Statistiques",
    items: [{ label: "Statistiques", path: "/admin/stats", icon: <BarChartIcon /> }],
  },
  {
    groupLabel: "Administration",
    items: [
      { label: "Gestion des tournois", path: "/admin/tournaments-management", icon: <EmojiEventsIcon /> },
      { label: "Droits d'administration", path: "/admin/admin-rights", icon: <AdminPanelSettingsIcon /> },
      { label: "Gestion des tables", path: "/admin/tables-management", icon: <TableRestaurantIcon /> },
      { label: "Documentation", path: "/admin/docs", icon: <MenuBookIcon /> },
    ],
  },
];

const Layout: FC<LayoutProps> = (props: LayoutProps) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(admin);
  const registrationPhase = useAppSelector(getRegistrationPhase);
  const [user] = useAuthState(auth);

  // "Inscription" n'est visible pour les non-admins qu'en phase open
  const isInscriptionVisible = isAdmin || registrationPhase === "open";

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleColorMode } = useColorMode();
  const isLiveKiosk =
    (location.pathname === "/live" || location.pathname === "/live-find") &&
    (isFullscreen || searchParams.get("kiosk") === "1");

  const visibleNavItems = useMemo(
    () =>
      navItems
        .filter((item) => !(item.hideOnMobile && isMobile))
        .filter(
          (item) => !(item.hideWhenRegistrationClosed && !isInscriptionVisible)
        )
        .filter(
          (item) =>
            !item.requiresClosedForPlayersPhase ||
            registrationPhase === "closedForPlayers"
        )
        .filter((item) => !item.requiresSignedIn || !!user),
    [isMobile, isInscriptionVisible, registrationPhase, user]
  );

  usePageTitle();

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const tournoiKeyParam = searchParams.get("tournoi");

  const fetchAdmin = useCallback(async () => {
    if (tournoiKeyParam) {
      await dispatch(fetchTournoiByKey(tournoiKeyParam));
    } else {
      await dispatch(fetchTournoi());
    }
    if (!user) return;
    const customClaims = await user.getIdTokenResult();
    const isAdminClaim = customClaims.claims.admin ? true : (false as boolean);
    dispatch(setAdmin(isAdminClaim));
  }, [dispatch, user, tournoiKeyParam]);

  useEffect(() => {
    fetchAdmin();
  }, [fetchAdmin]);

  const tournoi = useAppSelector(getTournoi);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const adminMenu = (
    <>
      <Divider
        orientation="vertical"
        sx={{
          bgcolor: "rgba(255,255,255,0.3)", // semi-transparent blanc
          height: 32,
          alignSelf: "center",
          mx: 2,
          width: "3px", // plus visible
        }}
        flexItem
      />
      <Button
        sx={{ color: "#fff" }}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        Admin
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#333",
            color: "#fff",
            borderRadius: 2,
            boxShadow: 5,
            minWidth: 200,
          },
        }}
      >
        {adminNavGroups.map((group) => (
          <React.Fragment key={group.groupLabel}>
            <MenuItem disabled sx={{ opacity: 1, color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", fontWeight: 600 }}>
              {group.groupLabel}
            </MenuItem>
            {group.items.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  handleClose();
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: "#555",
                  },
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  letterSpacing: 0.5,
                }}
              >
                <ListItemIcon sx={{ color: "#fff", minWidth: 32 }}>
                  {item.icon}
                </ListItemIcon>
                {item.label}
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
        height: "100%",
        backgroundColor: (t) =>
          t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.primary.main,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        py: 2,
      }}
    >
      <List>
        {visibleNavItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "center",
                  color: "#fff",
                  backgroundColor:
                    location.pathname === item.path
                      ? "rgba(255,255,255,0.2)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
                onClick={() => {
                  navigate(item.path);
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "1rem",
                    fontWeight: 500,
                    letterSpacing: 0.5,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        <ListItem key={"colorMode"} disablePadding>
          <ListItemButton
            sx={{
              textAlign: "center",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
            onClick={toggleColorMode}
          >
            <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
              {mode === "dark" ? (
                <Brightness7Icon />
              ) : (
                <Brightness4Icon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={mode === "dark" ? "Mode clair" : "Mode sombre"}
              primaryTypographyProps={{
                fontSize: "1rem",
                fontWeight: 500,
                letterSpacing: 0.5,
              }}
            />
          </ListItemButton>
        </ListItem>
        {user ? (
          <ListItem key={"logout"} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center", color: "#fff" }}
              onClick={() => {
                dispatch(clearSession());
                logout();
              }}
            >
              <ListItemText
                primary="Se déconnecter"
                primaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                }}
              />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem key={"login"} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center", color: "#fff" }}
              onClick={() => {
                navigate("/login");
              }}
            >
              <ListItemText
                primary="Se connecter"
                primaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        {isAdmin && (
          <>
            <Divider sx={{ bgcolor: "rgba(255,255,255,0.3)", my: 1 }} />
            <Chip
              label="ADMIN"
              color="secondary"
              variant="outlined"
              sx={{ my: 1 }}
            />
            <List>
              {adminNavGroups.map((group) => (
                <React.Fragment key={group.groupLabel}>
                  <ListSubheader
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      lineHeight: 2,
                      bgcolor: "transparent",
                    }}
                  >
                    {group.groupLabel}
                  </ListSubheader>
                  {group.items.map((item) => (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "center",
                          color: "#fff",
                          backgroundColor:
                            location.pathname === item.path
                              ? "rgba(255,255,255,0.2)"
                              : "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                          },
                        }}
                        onClick={() => {
                          navigate(item.path);
                        }}
                      >
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "1rem",
                            fontWeight: 500,
                            letterSpacing: 0.5,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Box
        component="a"
        href="#main-content"
        className="sr-only sr-only-focusable"
        sx={{
          zIndex: 9999,
          p: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        Aller au contenu principal
      </Box>
      <style>
        {`
      @keyframes fadeInDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}
      </style>
      {!isLiveKiosk && (
        <AppBar
          component="nav"
          sx={{
            animation: "fadeInDown 0.4s ease-out",
            py: 0.5,
            ...(theme.palette.mode === "dark" && {
              bgcolor: "grey.900",
              color: "#fff",
            }),
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              minHeight: { xs: 48, sm: 64 },
            }}
          >
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: "none" }, color: "#fff" }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.9,
                  transition: "opacity 0.3s ease",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
              onClick={() => navigate("/")}
              title="Retour à l'accueil"
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "8px",
                  backgroundColor: (theme) => theme.palette.background.paper,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  p: 0.5,
                  mr: 2,
                }}
              >
                <Box
                  component="img"
                  src="/sqyping-logo.jpg"
                  alt="SQY Ping Logo"
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "#fff", fontWeight: 600, letterSpacing: 1 }}
              >
                {tournoi?.label}
              </Typography>
            </Box>
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              {visibleNavItems.map((item) => (
                  <Button
                    key={item.label}
                    sx={{
                      color: "#fff",
                      fontWeight:
                        location.pathname === item.path ? "bold" : "normal",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        bottom: -2,
                        height: "2px",
                        width: location.pathname === item.path ? "100%" : "0%",
                        backgroundColor: "#fff",
                        transition: "width 0.3s ease-in-out",
                      },
                      "&:hover::after": {
                        width: "100%",
                      },
                    }}
                    onClick={() => {
                      navigate(item.path);
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              {isAdmin && adminMenu}
              <IconButton
                aria-label={mode === "dark" ? "Mode clair" : "Mode sombre"}
                onClick={toggleColorMode}
                sx={{ color: "#fff", ml: 0.5 }}
              >
                {mode === "dark" ? (
                  <Brightness7Icon />
                ) : (
                  <Brightness4Icon />
                )}
              </IconButton>
              {user ? (
                <Button
                  key={"logout"}
                  sx={{ color: "#fff" }}
                  onClick={() => {
                    dispatch(clearSession());
                    logout();
                  }}
                >
                  {"Se déconnecter"}
                </Button>
              ) : (
                <Button
                  key={"login"}
                  sx={{ color: "#fff" }}
                  onClick={() => {
                    navigate("/login");
                  }}
                >
                  {"Se connecter"}
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <Box component="nav" sx={{ display: { xs: "block", md: "none" } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          PaperProps={{
            sx: {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.primary.main,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
              borderBottomLeftRadius: 0,
              color: "#fff",
              py: 2,
            },
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        id="main-content"
        sx={{
          flex: 1,
          height: 0,
          p: isLiveKiosk ? 0 : 2,
          overflowY: isLiveKiosk ? "hidden" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {tournoi ? (
          <>
            {!isLiveKiosk && <PageBreadcrumbs />}
            <Outlet />
          </>
        ) : null}
      </Box>
      {!isLiveKiosk && (
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            textAlign: "center",
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body2" color="text.secondary" component="span">
            <Button
              component={Link}
              to="/contactus"
              variant="text"
              size="small"
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 0.5,
                verticalAlign: "baseline",
                textDecoration: "underline",
                textUnderlineOffset: "3px",
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[200]
                    : theme.palette.primary.main,
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.primary.dark,
                },
              }}
            >
              Nous contacter
            </Button>
            {" · Tournoi SQY Ping"}
          </Typography>
        </Box>
      )}
      <GlobalSnackbar />
    </Box>
  );
};

export default Layout;
